import { Component, ComponentType } from "react"
import { IncomingMessage } from 'http'
import { Me } from "domains/account"
import Axios, { AxiosRequestConfig } from "axios"
import getConfig from 'next/config';
import Router from "next/router"
import { NextPageContext, NextComponentType } from "next"
import { AppContext, AppProps } from "next/app"
import { CustomNextPage, PermissionLevels } from "lib/types"
import { AppTreeType } from "next/dist/shared/lib/utils"; 

const { publicRuntimeConfig: { baseRESTPort, basePort, baseProtocol }} = getConfig();

type getInitialProps = {
  getInitialProps?: (appContext: AppContext) => Promise<{ pageProps: AppProps }>
}

export type PermissionLevel = PermissionLevels.ADMIN | PermissionLevels.MEMBER | PermissionLevels.OWNER | PermissionLevels.OPEN | PermissionLevels.GUEST;

const hasRequiredRole = (allowedRoles: PermissionLevel[], userRole: PermissionLevel): boolean => {
  const findRole = allowedRoles.findIndex((role) => (
    role.toLocaleUpperCase() === userRole.toLocaleUpperCase()
  ));

  return findRole >= 0;
}

type CustomAppContext = {
  Component: NextComponentType<CustomNextPage, {}, {}> & {
    allowedRoles: PermissionLevel[];
  };
  AppTree: AppTreeType;
  ctx: NextPageContext;
  router: typeof Router;
};

const whitelist = [
  '/packs/x',
];

// Don't apply access checks to these routes
const skipList = [
  '/register',
  '/login',
]

const withAccessHandler = (WrappedApp: ComponentType<any> & getInitialProps) => {
  return class AccessHandler extends Component<any> {

    static async getInitialProps(appContext: CustomAppContext) {
      let pageProps: any = {}
      let appProps: any = {}
      const isServer = !!appContext.ctx.req;
      const allowedRoles = appContext.Component.allowedRoles;

      if (isServer && appContext.ctx.req.url.endsWith('/403')) {
        pageProps.statusCode = 403
      }

      if (isServer && appContext.ctx.req.url.endsWith('/404')) {
        pageProps.statusCode = 404
      }

      // A user could have created an account and logged, but not joined or created a team yet
      // In this case we need to call a different endpoint
      // The above happens during the registration flow

      // If there's no roles defined, it means the page doesn't exist
      if (!allowedRoles?.length) {
        appProps.pageProps = { ...(appProps?.pageProps || {}), ...pageProps };
        return { ...appProps };
      }

      const isSkippedRoute = skipList.find(
        (path) => appContext.ctx.req?.url.includes(path)
      );


      if (isServer && isSkippedRoute) {
        appProps.pageProps = { ...(appProps?.pageProps || {}), ...pageProps };
        return { ...appProps };
      }

      if (isServer && !pageProps.statusCode) {
        const req = appContext.ctx.req;
        const me = await getMe(req.headers.host, req);

        const isOpenPage = hasRequiredRole(allowedRoles, PermissionLevels.OPEN);
        
        // If it's open, we don't need to do any further checks
        if (!isOpenPage) {
          // If the user has no permissions.
          // They should only be allowed to view public pages
          if (!me?.permissions) {

            // They've created an account but are not part of a team
            // These users have GUEST access
            if (me?.user) {
              const isAuthorized = hasRequiredRole(allowedRoles, PermissionLevels.GUEST);

              if (!isAuthorized) {
                // Check if the requested path is in our whitelist
                const matchedWhitelistedPaths = whitelist.find(
                  (path) => appContext.ctx.req.url.startsWith(path)
                );

                if (matchedWhitelistedPaths) {
                  appProps.pageProps = { ...(appProps?.pageProps || {}), ...pageProps };
                  return { ...appProps }
                }

                appContext.ctx.res.writeHead(302, { Location: '/403' });
                appContext.ctx.res.end();
                return;
              }
            }

            // They've not created an account yet
            if (!me?.user) {
              const isAuthorized = hasRequiredRole(allowedRoles, PermissionLevels.OPEN);
              
              if (!isAuthorized) {
                // Check if the requested path is in our whitelist
                const matchedWhitelistedPaths = whitelist.find(
                  (path) => appContext.ctx.req.url.startsWith(path)
                );

                if (matchedWhitelistedPaths) {
                  appProps.pageProps = { ...(appProps?.pageProps || {}), ...pageProps };
                  return { ...appProps }
                }
                
                appContext.ctx.res.writeHead(302, { Location: '/login' });
                appContext.ctx.res.end();
                return;
              }
            }
          }          

          // If the user has permissions
          // We need to check wether they have the required permissions
          // to view the request route
          if (me.permissions) {
            let isAuthorized = false;

            // At the moment owners can view everything
            // So if they have owner permission, no further
            // checks are needed
            if (me.permissions === PermissionLevels.OWNER) {
              isAuthorized = true;
            }

            if (me.permissions === PermissionLevels.MEMBER) {
              isAuthorized = hasRequiredRole(allowedRoles, me.permissions);
            }

            if (me.permissions === PermissionLevels.ADMIN) {
              isAuthorized = hasRequiredRole(allowedRoles, me.permissions);
            }

            if (isAuthorized) {
              pageProps = { ...pageProps, me };
            }

            if (!isAuthorized) {
              appContext.ctx.res.writeHead(302, { Location: '/403' })
              appContext.ctx.res.end()
              return;
            }
          }
        }
        pageProps = { me };
      }

      appProps.pageProps = { ...(appProps?.pageProps || {}), ...pageProps };
      return { ...appProps }
    }

    render() {
      return (
        <WrappedApp {...this.props} />
      )
    }
  }
}

export const getMe = async (host: string, req?: IncomingMessage): Promise<Partial<Me> | undefined> => {
  host = host.replace(/(^\w+:|^)\/\//, '');

  let requestBaseUrl = host.replace(basePort || '80', baseRESTPort || '443');

  let config: AxiosRequestConfig = {
    baseURL: `${baseProtocol}://${requestBaseUrl}/v1/team-users`,
    headers: {},
    withCredentials: true,
  }

  if (req) {
    config = {
      ...config,
      headers: {
        ...config.headers,
        Cookie: req.headers.cookie,
      },
    }
  }

  try {
    const { data: me } = await Axios.get<Me>(`/me`, config);
    return me;
  } catch (error) {
    if (error?.response?.status === 421 || error?.response?.status === 401) {
      try {
        config = {
          ...config,
          baseURL: `${baseProtocol || 'http'}://${requestBaseUrl}/v1/user`,
        };
        
        const { data: me } = await Axios.get<Me['user']>('', config);

        return {
          user: me,
        }
      } catch (err) {
        return undefined;
      }
    }

    return undefined;
  }
}

export default withAccessHandler;
